import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import newCapTableApi from "./capTableApi";
import hissaApi from "./hissaApi";
import newEsopApi from "./newEsopApi";
import reportDownloadApi from "./captableReportDownload";
import {
  assertTokenUser,
  getEquionSession,
  isEquionSessionInvalid,
  redirectToIdentitySignIn,
  refetchAccessToken,
} from "./auth";
import { getAuthStore } from "../store/useAuthStore";

const idServerApi = axios.create({
  baseURL:
    process.env.REACT_APP_ID_SERVER_URL ||
    "https://identity-api.dev.hissa.com/v1/",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string> | null = null;

const isPublicAuthRequest = (url?: string) =>
  /\/(?:login|forgotPassword|globalSignUp|resetPassword)\/?$/i.test(
    url?.split("?")[0] || ""
  );

function attachTokenRefresh(
  client: AxiosInstance,
  header: "AccessToken" | "Authorization" = "AccessToken"
) {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig;
      if (
        error.response?.status === 401 &&
        originalRequest &&
        originalRequest.url !== "exit" &&
        !isPublicAuthRequest(originalRequest.url) &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        const session = getEquionSession();
        const requestToken = originalRequest.headers?.[header] as
          | string
          | undefined;
        try {
          if (!session) throw new Error("No active Identity session");
          assertTokenUser(
            requestToken || getAuthStore().accessToken || "",
            session
          );
        } catch {
          redirectToIdentitySignIn();
          return Promise.reject(error);
        }
        const pending = refreshPromise || refetchAccessToken(session);
        refreshPromise = pending;
        try {
          const newToken = await pending;
          const activeSession = getEquionSession();
          if (
            !activeSession ||
            activeSession.userId !== session.userId ||
            activeSession.sid !== session.sid
          ) {
            redirectToIdentitySignIn();
            return Promise.reject(error);
          }
          assertTokenUser(newToken, activeSession);
          sessionStorage.setItem("accesstoken", newToken);
          getAuthStore().setAuth({ accessToken: newToken });
          if (originalRequest.headers) {
            originalRequest.headers[header] = newToken;
            if (
              header === "AccessToken" &&
              originalRequest.headers.Authorization === requestToken
            ) {
              originalRequest.headers.Authorization = newToken;
            }
          }
          return client(originalRequest);
        } catch (refreshError) {
          if (isEquionSessionInvalid(refreshError)) {
            redirectToIdentitySignIn();
          }
          return Promise.reject(refreshError);
        } finally {
          if (refreshPromise === pending) refreshPromise = null;
        }
      }

      return Promise.reject(error);
    }
  );
}

attachTokenRefresh(newCapTableApi);
attachTokenRefresh(hissaApi);
attachTokenRefresh(reportDownloadApi);
attachTokenRefresh(newEsopApi, "Authorization");

export default idServerApi;
